


























import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import BulkPriceUpdateRequest from '@/models/BulkPriceUpdateRequest';
import moment from 'moment';
import PriceInput from './PriceInput.vue';
import { debounce } from 'lodash';
import { getCurrencySymbol, maximumAmountCurrency } from '@/utils/utils';
import TrackingUtils from '@/utils/tracking';
import AppTooltip from '@/components/AppTooltip.vue';

@Component({
  components: {AppTooltip, PriceInput },
})
export default class BoxRoomPrice extends Vue {

  get dropdownRoomStatus() {
    return this.$store.state.dashboard.dropdownRoomStatus.show;
  }

  get partnerId() {
    return this.$store.state.partner.id;
  }

  get formatedPrice() {
    return this.amountFromFormatted ? this.displayPrice(this.amountFromFormatted) : '-';
  }

  get amountFromFormatted() {
    const amountArr = this.amount.split(/[,.]/);
    return Math.round(Number(amountArr.join('.')) * 100);
  }

  get isSaturday() {
    const dayOfWeek = moment(this.date).format('d');
    return dayOfWeek === '6';
  }

  get isMonday() {
    const dayOfWeek = moment(this.date).format('d');
    return dayOfWeek === '1';
  }

  @Prop({ required: true, default: 0 }) public initialAmount!: number;
  @Prop({ required: true }) public readonly roomId!: string;
  @Prop({ required: true }) public readonly date!: string;
  @Prop({ required: true }) public readonly disabled!: boolean;
  @Prop({ required: true }) public readonly currencyCode!: string;

  public isEditingPrice: boolean = false;
  public amountNumber: number = this.initialAmount;
  public amount: string = this.displayPrice(this.initialAmount);
  public prevAmountNumber: number = this.amountNumber;
  public maxAmount = maximumAmountCurrency[this.currencyCode];
  public isLoading: boolean = false;
  public inputShown: boolean = false;
  public errorInput: boolean = false;
  public showUpdateTooltip: boolean = false;
  public tooltipPosition: { top: number, left: number } = { top: 0, left: 0 };

  public showError: any = debounce(() => {
    this.$store.dispatch('addToast', {
      type: 'error',
      message: 'messages.error.invalidPriceInput',
      replace: {currency: getCurrencySymbol(this.currencyCode), max: this.maxAmount / 100},
      timeout: 3,
    });
  }, 1000);

  public showInput() {
    if (this.disabled) {
      this.inputShown = false;
      return;
    }
    this.isEditingPrice = true;
    this.inputShown = true;
  }

  public handleError(hasError: boolean) {
    this.errorInput = hasError;
    if (hasError) {
      this.showError(hasError);
    }
  }

  public async updatePrice(event: Event) {
    if (event.type === 'blur') {
      const inputElement = (event.target as HTMLInputElement);
      const rect = inputElement.getBoundingClientRect();

      if (this.errorInput || this.amountNumber === this.prevAmountNumber || this.isLoading) {
        this.errorInput = false;
        this.cancelUpdate();
        return false;
      } else {
        const payload = new BulkPriceUpdateRequest(
          this.$props.roomId,
          this.amountFromFormatted,
          false,
        );
        payload.startDate = this.date;
        payload.endDate = this.date;
        try {
          this.isLoading = true;
          const prevPrice = this.prevAmountNumber;
          const newPrice = this.amountNumber;
          await this.$store.dispatch('bulkPriceUpdate', payload);
          if (this.$gtm.dataLayer() ) {
            const percentage = TrackingUtils.getPercentage(prevPrice, newPrice);
            this.$gtm.dataLayer().push({
              event: newPrice > prevPrice ? 'extraNightIncrease' : 'extraNightDecrease',
              partnerId: this.partnerId,
              status: {
                value: newPrice / 100,
                valueRange: TrackingUtils.getPriceRange(newPrice),
                priceUpdateDate: moment(this.date).format('DD-MM-YYYY'),
                incrementalValue: `${percentage}%`,
                incrementalValueRange: TrackingUtils.getPercentageRange(percentage),
              },
            });
          }

          this.tooltipPosition = {
            top: rect.top,
            left: rect.left - 10,
          };
          this.showUpdateTooltip = true;
          setTimeout(() => {
            this.showUpdateTooltip = false;
          }, 3000);
        } catch (error) {
          const parseError = error as {status: number, body: {detail: string}};
          if (parseError.status === 400) {
            this.isLoading = false;
            const [message, amount, maxAmountAllowed] = parseError.body.detail.match(/The value of Amount "([^"]+)" cannot be greater than "([^"]+)"/) || [];
            this.$store.dispatch('addToast', {
              type: 'error',
              message: 'messages.error.maxAmountAllowed',
              replace: {
                amount,
                maxAmountAllowed,
              },
              timeout: 5,
            });
            this.amountNumber = this.prevAmountNumber;
          }
        } finally {
          this.isEditingPrice = false;
          this.inputShown = false;
          this.isLoading = false;
          this.prevAmountNumber = this.amountNumber;
        }
      }
    } else {
      this.isEditingPrice = false;
      this.inputShown = false;
      (event.currentTarget as HTMLInputElement).blur();
    }
  }

  public cancelUpdate() {
    this.isEditingPrice = false;
    this.inputShown = false;
    this.amount = this.displayPrice(this.prevAmountNumber);
    this.amountNumber = this.prevAmountNumber;
  }

  public displayPrice(price: number): string {
    const separator = this.$store.getters.separator;
    return price % 100 === 0 ?
      String(price / 100).split('.').join(separator) :
      String((price / 100).toFixed(2)).split('.').join(separator);
  }

  @Watch('isEditingPrice', {immediate: true})
  public onEditing(newValue: number) {
    if (newValue && this.dropdownRoomStatus) {
      this.$dropdown.hide();
    }
  }

  @Watch('amount', {immediate: true})
  private onAmountChange(value: number, oldVal: number) {
    if (value !== oldVal) {
      this.amountNumber = this.amountFromFormatted;
    }
  }

  @Watch('initialAmount', {immediate: true})
  private onInitAmountChange(newValue: number, oldVal: number) {
    if (newValue !== oldVal) {
      this.amount = this.displayPrice(newValue);
      this.amountNumber = newValue;
      this.prevAmountNumber = this.amountNumber;
    }
  }
}
